// TimeRangeRow.tsx
import * as React from "react";
import { Box, Typography } from "@mui/material";
import CompactHoursSelect from "./CompactHoursSelect";
import { useMemo } from "react";
import { DEFAULT_PERIOD_MINUTES } from "./AlertHoursPicker";

export type MinuteOption = { label: string; value: number };

export interface TimeRangeRowProps {
  start: number;
  end: number;
  options: MinuteOption[];
  disabled?: boolean;
  renderValue: (v: number) => string;
  onChange: (next: { start: number; end: number }) => void;
  startWidth?: string;
  endWidth?: string;
  gap?: number;
}

export const TimeRangeRow: React.FC<TimeRangeRowProps> = React.memo(
  ({
    start,
    end,
    options,
    disabled,
    renderValue,
    onChange,
    startWidth = "75px",
    endWidth = "75px",
    gap = 1,
  }) => {
    const handleStart = (val: number) => {
      if (val >= end) {
        onChange({ start: val, end: val + 30 });

        return;
      }

      onChange({ start: val, end });
    };
    const handleEnd = (val: number) => onChange({ start, end: val });

    const withOutLastOption = useMemo(
      () => options.filter(o => o.value !== DEFAULT_PERIOD_MINUTES),
      [options]
    );

    const withOutFirstOption = useMemo(
      () =>
        options
          .filter(o => o.value !== 0)
          .map(o => ({
            ...o,
            label: o.label === "12:00am" ? "11:59pm" : o.label,
          })),
      [options]
    );

    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `${startWidth} 20px ${endWidth}`,
          columnGap: gap,
          alignItems: "center",
        }}
      >
        <Box>
          <CompactHoursSelect
            value={start}
            onChange={handleStart}
            options={withOutLastOption}
            disabled={disabled}
            renderValue={renderValue}
          />
        </Box>

        <Typography variant="body2Regular" sx={{ textAlign: "center" }}>
          to
        </Typography>

        <Box>
          <CompactHoursSelect
            value={end}
            minValue={start + 30}
            onChange={handleEnd}
            options={withOutFirstOption}
            disabled={disabled}
            renderValue={renderValue}
          />
        </Box>
      </Box>
    );
  }
);

TimeRangeRow.displayName = "TimeRangeRow";
